import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { getSessionId } from "./authService";

export async function createPublicSiteLead(siteId, payload) {
  const sid = String(siteId || "").trim();
  if (!sid) {
    throw new Error("public_site_id_required");
  }
  if (/^\d+$/.test(sid)) {
    throw new Error("public_site_token_required");
  }
  const resolvedUrl = resolveApiUrl(
    `/api/public-sites/by-token/${encodeURIComponent(sid)}/leads`
  );
  const requestConfig = {
    withCredentials: true,
    timeout: 20000,
    headers: { "Content-Type": "application/json" },
  };
  console.log("REQUEST CONFIG", requestConfig);
  const { data } = await axios.post(
    resolvedUrl,
    payload || {},
    requestConfig
  );
  return data;
}

/**
 * @param {{ jsonContentType?: boolean }} [options]
 * - GET requests: pass jsonContentType: false — no body and avoid 415 from servers that
 *   try to parse JSON when Content-Type is application/json.
 */
async function withAgentSessionConfig(options = {}) {
  const jsonContentType = options.jsonContentType !== false;
  const timeoutMs =
    Number.isFinite(Number(options.timeout)) && Number(options.timeout) > 0
      ? Math.floor(Number(options.timeout))
      : 20000;
  const sessionID = await getSessionId();
  if (!sessionID) {
    throw new Error("public_leads_no_session");
  }
  const headers = {
    "X-Session-ID": String(sessionID).trim(),
  };
  if (jsonContentType) {
    headers["Content-Type"] = "application/json";
  }
  return {
    headers,
    withCredentials: true,
    timeout: timeoutMs,
  };
}

export async function fetchAgentPublicLeads(params = {}) {
  const cfg = await withAgentSessionConfig({
    jsonContentType: false,
    // Remote RDS without conn-reuse was ~26s; BE now reuses conn per request.
    // Keep headroom above cold-start / concurrent CRM polls.
    timeout: 45000,
  });
  const axiosParams = {};
  if (params?.status) axiosParams.status = String(params.status);
  if (params?.limit != null) axiosParams.limit = Math.min(Number(params.limit) || 100, 100);
  if (params?.scope === "all") axiosParams.scope = "all";
  if (params?.kind === "dockets" || params?.docketsOnly === true) {
    axiosParams.kind = "dockets";
  }
  const url = resolveApiUrl("/api/agents/me/public-leads");
  const requestConfig = { ...cfg, params: axiosParams };
  const { data } = await axios.get(url, requestConfig);
  return data;
}

export async function fetchAgentPublicLeadsSummary() {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  console.log("REQUEST CONFIG", cfg);
  const { data } = await axios.get(
    resolveApiUrl("/api/agents/me/public-leads/summary"),
    cfg
  );
  return data;
}

/**
 * Admin assign / unassign a public lead (platform-admin session).
 * @param {string} leadId
 * @param {number|null} agentId — agent id, or null to return to the admin pool
 */
export async function assignAdminPublicLead(leadId, agentId) {
  const cfg = await withAgentSessionConfig();
  const { data, status } = await axios.patch(
    resolveApiUrl(
      `/api/admin/public-leads/${encodeURIComponent(String(leadId || ""))}/assign`
    ),
    { agent_id: agentId == null ? null : Number(agentId) },
    cfg
  );
  return { data, status };
}

/**
 * Admin bulk assign / unassign — calls single-lead assign in small parallel batches.
 * @param {Array<string|number>} leadIds
 * @param {number|null} agentId
 * @param {{ batchSize?: number }} [options]
 * @returns {Promise<{
 *   okIds: string[],
 *   failed: Array<{ leadId: string, message: string }>,
 *   leadsById: Record<string, object>,
 * }>}
 */
export async function assignAdminPublicLeadsBulk(leadIds, agentId, options = {}) {
  const ids = (Array.isArray(leadIds) ? leadIds : [])
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  const uniqueIds = [...new Set(ids)];
  const batchSizeRaw = Number(options?.batchSize);
  const batchSize =
    Number.isFinite(batchSizeRaw) && batchSizeRaw > 0
      ? Math.min(Math.floor(batchSizeRaw), 10)
      : 5;
  const nextAgentId =
    agentId == null || agentId === "" ? null : Number(agentId);
  if (nextAgentId != null && !Number.isFinite(nextAgentId)) {
    throw new Error("invalid_agent_id");
  }

  const okIds = [];
  const failed = [];
  const leadsById = {};

  for (let i = 0; i < uniqueIds.length; i += batchSize) {
    const batch = uniqueIds.slice(i, i + batchSize);
    const settled = await Promise.allSettled(
      batch.map((leadId) => assignAdminPublicLead(leadId, nextAgentId))
    );
    settled.forEach((result, idx) => {
      const leadId = batch[idx];
      if (result.status === "fulfilled") {
        const updated = result.value?.data?.lead;
        if (updated?.id) leadsById[String(updated.id)] = updated;
        okIds.push(leadId);
        return;
      }
      const err = result.reason;
      failed.push({
        leadId,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "שגיאה בהקצאת הליד",
      });
    });
  }

  return { okIds, failed, leadsById };
}

export async function patchAgentPublicLead(leadId, payload) {
  const cfg = await withAgentSessionConfig();
  console.log("REQUEST CONFIG", cfg);
  const { data, status } = await axios.patch(
    resolveApiUrl(`/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}`),
    payload || {},
    {
      ...cfg,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400 && !data?.success) {
    return {
      success: false,
      error: data?.error || "PUBLIC_LEADS_PATCH_ERROR",
      message: data?.message || "שגיאה בעדכון ליד",
      status,
      ...data,
    };
  }
  return data;
}

export async function createAgentManualPublicLead(payload = {}) {
  const cfg = await withAgentSessionConfig();
  const { data } = await axios.post(
    resolveApiUrl("/api/agents/me/public-leads"),
    payload || {},
    cfg
  );
  return data;
}

export async function createAgentPublicLeadFromOrder(orderId) {
  const cfg = await withAgentSessionConfig();
  const { data } = await axios.post(
    resolveApiUrl("/api/agents/me/public-leads/from-order"),
    { order_id: String(orderId || "").trim() },
    cfg
  );
  return data;
}

export async function deleteAgentPublicLead(leadId) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const { data } = await axios.delete(
    resolveApiUrl(`/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}`),
    cfg
  );
  return data;
}

export async function fetchAgentPublicLead(leadId, params = {}) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const axiosParams = {};
  if (params?.includeBooking) axiosParams.include_booking = "1";
  const { data } = await axios.get(
    resolveApiUrl(`/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}`),
    { ...cfg, params: axiosParams, timeout: params?.timeout ?? 25000 }
  );
  return data;
}

export async function fetchAgentPublicLeadNotes(leadId, params = {}) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const axiosParams = {};
  if (params?.limit != null) axiosParams.limit = params.limit;
  const url = resolveApiUrl(
    `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/notes`
  );
  const requestConfig = { ...cfg, params: axiosParams };
  console.log("REQUEST CONFIG", requestConfig);
  const { data } = await axios.get(url, requestConfig);
  return data;
}

export async function createAgentPublicLeadNote(leadId, payload) {
  const cfg = await withAgentSessionConfig();
  console.log("REQUEST CONFIG", cfg);
  const { data, status } = await axios.post(
    resolveApiUrl(`/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/notes`),
    payload || {},
    {
      ...cfg,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400) {
    const err = new Error(
      data?.message || data?.error?.message || data?.error || "note_create_failed"
    );
    err.payload = data;
    err.status = status;
    throw err;
  }
  return data;
}

export async function fetchAgentPublicLeadFiles(leadId, params = {}) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const axiosParams = {};
  if (params?.limit != null) axiosParams.limit = params.limit;
  const { data } = await axios.get(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/files`
    ),
    { ...cfg, params: axiosParams }
  );
  return data;
}

/**
 * Upload an extra PDF attachment for a CRM lead / docket.
 * @param {string} leadId
 * @param {File|Blob} file
 * @param {string} [filename]
 */
export async function uploadAgentPublicLeadFile(leadId, file, filename) {
  const sessionID = await getSessionId();
  if (!sessionID) throw new Error("public_leads_no_session");
  const form = new FormData();
  const name =
    filename ||
    (file && typeof file === "object" && "name" in file ? file.name : null) ||
    "document.pdf";
  form.append("file", file, name);
  const { data, status } = await axios.post(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/files`
    ),
    form,
    {
      headers: {
        "X-Session-ID": String(sessionID).trim(),
        // Let browser set multipart boundary — do not set Content-Type manually
      },
      withCredentials: true,
      timeout: 60000,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400 || !data?.success) {
    const err = new Error(
      data?.message || data?.error?.message || data?.error || "file_upload_failed"
    );
    err.payload = data;
    err.status = status;
    throw err;
  }
  return data;
}

/** Authenticated blob URL for previewing/downloading a lead extra file. */
export async function fetchAgentPublicLeadFileBlobUrl(leadId, fileId, { download = false } = {}) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const { data } = await axios.get(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/files/${encodeURIComponent(String(fileId || ""))}`
    ),
    {
      ...cfg,
      params: download ? { download: 1 } : undefined,
      responseType: "blob",
      timeout: 60000,
    }
  );
  const blob =
    data instanceof Blob
      ? data
      : new Blob([data], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}

/** CRM docket payment ledger (display adjustments — not order SSOT). */
export async function fetchAgentPublicLeadPaymentEntries(leadId, params = {}) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const axiosParams = {};
  if (params?.limit != null) axiosParams.limit = params.limit;
  if (params?.includeCancelled) axiosParams.include_cancelled = "1";
  const { data } = await axios.get(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/payment-entries`
    ),
    { ...cfg, params: axiosParams }
  );
  return data;
}

/**
 * @param {string} leadId
 * @param {{
 *   method?: string,
 *   amount_ils?: number,
 *   direction?: 'credit'|'debit',
 *   note?: string,
 *   order_id?: string,
 *   entry_kind?: 'manual'|'hide_auto',
 *   hides_auto_key?: string,
 *   entry_date?: string,
 * }} payload
 */
export async function createAgentPublicLeadPaymentEntry(leadId, payload = {}) {
  const cfg = await withAgentSessionConfig();
  const { data, status } = await axios.post(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/payment-entries`
    ),
    payload || {},
    {
      ...cfg,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400 || !data?.success) {
    const err = new Error(
      data?.message || data?.error || "payment_entry_create_failed"
    );
    err.payload = data;
    err.status = status;
    throw err;
  }
  return data;
}

/** Soft-cancel a CRM payment ledger row. */
export async function cancelAgentPublicLeadPaymentEntry(leadId, entryId) {
  const cfg = await withAgentSessionConfig({ jsonContentType: false });
  const { data, status } = await axios.delete(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/payment-entries/${encodeURIComponent(String(entryId || ""))}`
    ),
    {
      ...cfg,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400 || !data?.success) {
    const err = new Error(
      data?.message || data?.error || "payment_entry_cancel_failed"
    );
    err.payload = data;
    err.status = status;
    throw err;
  }
  return data;
}

/**
 * Email selected extra lead PDF files to client(s).
 * @param {string} leadId
 * @param {{ toEmails: string[], fileIds: string[], orderId?: string }} payload
 */
export async function sendAgentPublicLeadFiles(leadId, payload = {}) {
  const cfg = await withAgentSessionConfig();
  const body = {
    to_emails: Array.isArray(payload.toEmails) ? payload.toEmails : [],
    file_ids: Array.isArray(payload.fileIds) ? payload.fileIds : [],
  };
  if (payload.orderId) body.order_id = String(payload.orderId);
  const { data, status } = await axios.post(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/files/send`
    ),
    body,
    {
      ...cfg,
      timeout: 90000,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400 && data?.status !== "partial") {
    const err = new Error(
      data?.message || data?.error || data?.status || "file_send_failed"
    );
    err.payload = data;
    err.status = status;
    throw err;
  }
  return data;
}

/**
 * Manual סיכום הזמנה PDF for a CRM lead (works without linked order — Mano etc.).
 * @param {string} leadId
 * @param {Record<string, string>} fields
 * @returns {Promise<Blob>}
 */
export async function fetchAgentLeadOrderSummaryPdf(leadId, fields = {}) {
  const cfg = await withAgentSessionConfig();
  const { data, status } = await axios.post(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/order-summary`
    ),
    { fields: fields || {} },
    {
      ...cfg,
      responseType: "blob",
      timeout: 90000,
      validateStatus: (s) => s === 200 || s < 500,
    }
  );
  if (status !== 200) {
    let msg = "order_summary_failed";
    try {
      const text = data instanceof Blob ? await data.text() : String(data || "");
      const parsed = JSON.parse(text);
      msg = parsed?.message || parsed?.error || msg;
    } catch {
      /* keep default */
    }
    throw new Error(msg);
  }
  return new Blob([data], { type: "application/pdf" });
}

/**
 * Email manual סיכום הזמנה for a CRM lead.
 * @param {string} leadId
 * @param {{ toEmail: string, fields: Record<string, string> }} payload
 */
export async function sendAgentLeadOrderSummary(leadId, payload = {}) {
  const cfg = await withAgentSessionConfig();
  const { data, status } = await axios.post(
    resolveApiUrl(
      `/api/agents/me/public-leads/${encodeURIComponent(String(leadId || ""))}/order-summary/send`
    ),
    {
      to_email: payload.toEmail || payload.email || "",
      fields: payload.fields || {},
    },
    {
      ...cfg,
      timeout: 90000,
      validateStatus: (s) => s < 500,
    }
  );
  if (status >= 400 || data?.success === false) {
    const err = new Error(
      data?.message || data?.error || "order_summary_send_failed"
    );
    err.payload = data;
    err.status = status;
    throw err;
  }
  return data;
}
